import React, { useState, useEffect } from 'react'
import { Button, Modal, message } from 'antd'
import { MobileOutlined, CloseOutlined } from '@ant-design/icons'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    // 检查是否已经安装
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isInWebAppiOS = (window.navigator as any).standalone === true
    if (isStandalone || isInWebAppiOS) {
      setIsInstalled(true)
      return
    }

    // 检测 iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
    setIsIOS(iOS)

    // 监听 beforeinstallprompt 事件（Android/Chrome）
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      
      // 延迟显示提示，避免打扰用户
      setTimeout(() => {
        const hasSeenPrompt = localStorage.getItem('pwa-install-prompt-seen')
        if (!hasSeenPrompt) {
          setIsModalVisible(true)
        }
      }, 3000)
    }

    // 监听安装完成事件
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsModalVisible(false)
      message.success('应用已成功安装！')
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const handleInstall = async () => {
    if (deferredPrompt) {
      // 显示浏览器安装提示
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        message.success('正在安装应用...')
      }
      
      setDeferredPrompt(null)
      setIsModalVisible(false)
      localStorage.setItem('pwa-install-prompt-seen', 'true')
    }
  }

  const handleClose = () => {
    setIsModalVisible(false)
    localStorage.setItem('pwa-install-prompt-seen', 'true')
  }

  // 如果已安装或没有提示事件，不显示
  if (isInstalled || (!deferredPrompt && !isIOS)) {
    return null
  }

  return (
    <Modal
      open={isModalVisible}
      onCancel={handleClose}
      footer={null}
      closeIcon={<CloseOutlined />}
      width={400}
      className="pwa-install-modal"
    >
      <div className="text-center py-4">
        <MobileOutlined className="text-5xl text-blue-500 mb-4" />
        <h3 className="text-xl font-semibold mb-2">安装应用</h3>
        <p className="text-gray-600 mb-6">
          {isIOS ? (
            <>
              点击 <span className="font-semibold">分享</span> 按钮，然后选择{' '}
              <span className="font-semibold">"添加到主屏幕"</span>
            </>
          ) : (
            '将应用添加到主屏幕，获得更好的体验'
          )}
        </p>
        {deferredPrompt && (
          <Button
            type="primary"
            size="large"
            onClick={handleInstall}
            className="w-full"
          >
            立即安装
          </Button>
        )}
        {isIOS && (
          <div className="text-sm text-gray-500 mt-4">
            <p>Safari 浏览器：</p>
            <ol className="list-decimal list-inside mt-2 space-y-1">
              <li>点击底部的分享图标</li>
              <li>选择"添加到主屏幕"</li>
              <li>点击"添加"</li>
            </ol>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default PWAInstallPrompt
